'use strict';

const container = document.querySelector('#main'),
    lang = document.querySelector('body.page-lang-es') ? 'es' : 'en',
    carousels = document.querySelectorAll('.carousel'),
    text = {
        en: {
            skip: 'Skip to end of carousel',
            carouselRoleDesc: 'carousel',
            slideRoleDesc: 'slide',
            slideLabel: (i, length) => `${i + 1} of ${length}`,
            buttonLabel: {
                next: 'display next slide',
                prev: 'display previous slide'
            },
            slideControls: 'Slide controls',
            dotLabel: (i, length) => `Display slide ${i + 1} of ${length}`,
            liveAlert: {
                image: 'Now showing',
                caption: 'Content underneath reads:'
            }
        },
        es: {
            skip: 'Skip to end of carousel',
            carouselRoleDesc: 'carousel',
            slideRoleDesc: 'slide',
            slideLabel: (i, length) => `${i + 1} of ${length}`,
            buttonLabel: {
                next: 'display next slide',
                prev: 'display previous slide'
            },
            slideControls: 'Slide controls',
            dotLabel: (i, length) => `Display slide ${i + 1} of ${length}`,
            liveAlert: {
                image: 'Now showing',
                caption: 'Content underneath reads:'
            }
        }
    };

// Find the Base URL
const baseUrl = () => {
    try {
        const [url] = import.meta.url.split('/scripts');
        if (url) return new URL(url + '/', location.href).href;
    } catch {}
    try {
        for (const link of document.querySelectorAll('link[rel=stylesheet]')) {
            if ((link.href || '').match(/niddk-web\.css/i)) {
                const url = new URL(link.href, location.href);
                url.pathname = url.pathname.split('/styles')[0] + '/';
                url.search = '';
                url.hash = '';
                return url.href;
            }
        }
    } catch {}
    return new URL('/', location.href).href;
};

// Load stylesheet if not provided
const checkStyleSheet = () => {
    return new Promise((resolve, reject) => {
        if (
            !document.querySelector(
                'link[rel="stylesheet"][href$="/carousel.css"]'
            )
        ) {
            carousels.forEach((carousel) => carousel.classList.add('cs-delay'));
            const link = document.createElement('link');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', `${baseUrl()}/styles/carousel.css`);
            link.onload = resolve;
            link.onerror = () =>
                reject(new Error(`Failed to load /carousel.css`));
            document.head.append(link);
            carousels.forEach((carousel) =>
                setTimeout(() => {
                    carousel.classList.remove('cs-delay');
                }, 600)
            );
        } else resolve();
    });
};

const carouselSetup = async () => {
    // Wait for stylesheet to load
    await checkStyleSheet();

    carousels.forEach((carousel) => {
        const nextButton = carousel.querySelector('.btn-next'),
            prevButton = carousel.querySelector('.btn-prev'),
            slides = carousel.querySelectorAll('.carousel-slide'),
            dotNav = carousel.querySelector('.cs-dots'),
            dots = [],
            skipId = 'id' + Math.floor(Math.random() * 1000000).toString(36);

        let currentIndex = 0,
            isDragging = false,
            offsetX,
            startX;

        // Update the carousel width and height based on the size of the container
        const updateCarouselSize = () => {
            const width =
                container.offsetWidth < 650
                    ? container.offsetWidth
                    : carousel.offsetWidth;
            carousel.style.setProperty('--carousel-width', `${width}px`);

            let contentHeight = 0;
            slides.forEach((slide) => {
                const box = slide.querySelector('.cs-box .content');
                box.style = '';

                let newHeight = 0;
                newHeight = box.getBoundingClientRect().height;

                if (newHeight > contentHeight) contentHeight = newHeight;
            });
            slides.forEach((slide) =>
                slide
                    .querySelector('.cs-box .content')
                    .style.setProperty('height', `${contentHeight}px`)
            );
            slides.forEach((slide) => {
                slide.style = '';
            });

            carousel.style.setProperty(
                '--content-height',
                `${contentHeight}px`
            );
        };

        // Update the current status of the dot counter
        const updateDots = () => {
            dots.forEach((dot, i) => {
                dot.classList.toggle('cs-current', i === currentIndex);
                dot.setAttribute('aria-current', i === currentIndex);
                dot.setAttribute('aria-disabled', i === currentIndex);
            });
        };

        // Animation on slide change
        const snapSlide = (slideIndex, offset) => {
            if (slideIndex >= 0 && slideIndex < slides.length) {
                slides[slideIndex].style.transition =
                    'transform 0.25s ease-in-out';
                slides[
                    slideIndex
                ].style.transform = `translate(-50%, -50%) translateX(${offset}px)`;
            }
        };

        const resetSlides = () => {
            // Snap back to original position
            snapSlide(currentIndex, 0);
            // Snap next slide
            snapSlide(currentIndex + 1, carousel.offsetWidth);
            // Snap previous slide
            snapSlide(currentIndex - 1, -carousel.offsetWidth);
        };

        // Navigate to a new carousel slide
        // newSlide is the index of the new current slide
        // delay is true if delaying the z-index updates to the slides
        const changeSlide = (newSlide, delay) => {
            // Set current slide to chosen index
            currentIndex = newSlide;

            updateDots();

            // Apply the outside positioning class for any slides two positions
            // away from the current slide depending on the navigational
            // direction chosen
            slides.forEach((slide, index) => {
                // Check the current statuses of each slide
                const isCurrent = slide.classList.contains('cs-current'),
                    isNext = slide.classList.contains('cs-next'),
                    isPrev = slide.classList.contains('cs-prev'),
                    isFinal = slide.classList.contains('cs-final'),
                    isTop = slide.classList.contains('cs-top'),
                    isInert = slide.inert;

                // Reset the inline transforms
                slide.style = '';

                // Set the selected slide to current
                if (index === currentIndex) {
                    slide.classList.add('cs-current');
                    if (isTop)
                        setTimeout(
                            () => {
                                slide.classList.remove('cs-top');
                            },
                            delay ? 333 : 0
                        );

                    if (isNext) slide.classList.remove('cs-next');
                    if (isPrev) slide.classList.remove('cs-prev');
                    if (isInert) slide.removeAttribute('inert');
                    // Add the current slide's tab index so it can be tabbed to
                    slide.tabIndex = '0';
                    // Focus on the new current slide
                    // slide.focus();
                } else {
                    // Remove the current slide's class designation
                    if (isCurrent) slide.classList.remove('cs-current');
                    if (!isInert) slide.setAttribute('inert', '');
                    // Remove the current slide's tab index so it can't be tabbed to
                    slide.tabIndex = '-1';
                }

                // Adjust classes for all slides before new chosen index
                if (index < currentIndex) {
                    if (isNext) slide.classList.remove('cs-next');
                    if (!isPrev) slide.classList.add('cs-prev');
                }

                // Adjust classes for all slides after new chosen index
                if (index > currentIndex) {
                    if (isPrev) slide.classList.remove('cs-prev');
                    if (!isNext) slide.classList.add('cs-next');
                }

                // Handle the z-index of the current's adjacent slides
                if (index === currentIndex - 1 || index === currentIndex + 1) {
                    setTimeout(
                        () => {
                            slide.classList[isTop ? 'remove' : 'add']('cs-top');
                        },
                        delay ? 333 : 0
                    );
                }

                // Handle the outer slides
                if (index < currentIndex - 1 || index > currentIndex + 1) {
                    if (!isFinal) slide.classList.add('cs-final');
                    if (isTop) {
                        setTimeout(
                            () => {
                                slide.classList.remove('cs-top');
                            },
                            delay ? 333 : 0
                        );
                    }
                    slide.setAttribute('aria-hidden', 'true');
                } else {
                    slide.classList.remove('cs-final');
                    slide.setAttribute('aria-hidden', 'false');
                }
            });

            // Disable navigation buttons if there are no more next or
            // previous slides to navigate to
            prevButton.disabled = currentIndex === 0;
            prevButton.tabIndex = currentIndex === 0 ? '-1' : '0';
            prevButton.setAttribute('aria-disabled', currentIndex === 0);
            nextButton.disabled = currentIndex === slides.length - 1;
            nextButton.tabIndex =
                currentIndex === slides.length - 1 ? '-1' : '0';
            nextButton.setAttribute(
                'aria-disabled',
                currentIndex === slides.length - 1
            );

            // Screen reader alert when navigating slides
            const live = carousel.querySelector('.cs-alert');
            live.innerHTML = `${text[lang].liveAlert.image} ${slides[
                currentIndex
            ]
                .querySelector('img')
                .getAttribute('alt')}. ${text[lang].liveAlert.caption} ${
                slides[currentIndex].querySelector('.content').innerHTML
            }`;
            live.querySelectorAll('a').forEach((a) => (a.tabIndex = '-1'));
        };

        // On window load, measure slides, create dots, and bind drag events
        window.addEventListener('load', () => {
            const skipLink = document.createElement('a'),
                skipEnd = document.createElement('div'),
                live = document.createElement('div');
            skipLink.innerHTML = text[lang].skip;
            skipLink.setAttribute('href', `#${skipId}`);
            skipLink.classList.add('skip-link');
            skipEnd.setAttribute('id', skipId);
            live.classList.add('cs-alert', 'sr-only');
            live.setAttribute('role', 'status');
            live.setAttribute('aria-live', 'polite');

            carousel.insertBefore(skipLink, carousel.firstChild);
            carousel.append(skipEnd);
            carousel.append(live);
            carousel.setAttribute(
                'aria-roledescription',
                text[lang].carouselRoleDesc
            );

            prevButton.setAttribute('aria-disabled', currentIndex === 0);

            //Set up tab focus
            carousel.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    carousel.classList.add('cs-tabbed');

                    if (document.activeElement === slides[currentIndex]) {
                        slides[currentIndex]
                            .querySelectorAll('a')
                            .forEach((a) => (a.tabIndex = '0'));
                    } else if (document.activeElement.tagName !== 'A') {
                        slides[currentIndex]
                            .querySelectorAll('a')
                            .forEach((a) => (a.tabIndex = '-1'));
                    }
                }
            });

            dotNav.setAttribute('role', 'group');
            dotNav.setAttribute('aria-label', text[lang].slideControls);

            // Button navigation
            nextButton.addEventListener('click', () => {
                changeSlide(currentIndex + 1, true);
            });
            prevButton.addEventListener('click', () => {
                changeSlide(currentIndex - 1, true);
            });
            nextButton.setAttribute('aria-label', text[lang].buttonLabel.next);
            prevButton.setAttribute('aria-label', text[lang].buttonLabel.prev);

            // Handle default drag behavior
            const handleDragStart = (e) => {
                e.preventDefault();
            };

            // Handle the transform based on drag distance
            const handlePointerMove = (e) => {
                if (!isDragging) return;

                const x = e.pageX - carousel.offsetLeft,
                    walk = x - offsetX,
                    prevWalk =
                        carousel.offsetWidth * -1 +
                        walk / (walk > 0 ? 1.5 : 0.875),
                    nextWalk =
                        carousel.offsetWidth + walk / (walk > 0 ? 0.875 : 1.5);

                // Current slide moves in sync with finger/mouse
                slides[
                    currentIndex
                ].style.transform = `translate(-50%,-50%) translateX(${walk}px)`;
                slides[currentIndex].style.transition = `transform linear`;

                // // Slides move slightly out of sync with finger/mouse
                const slideTransition = (slideIndex, offset) => {
                    if (slideIndex >= 0 && slideIndex < slides.length) {
                        slides[
                            slideIndex
                        ].style.transform = `translate(-50%, -50%) translateX(${offset}px)`;
                        slides[slideIndex].style.transition =
                            'transform linear';
                    }
                };

                // Then in your pointermove (or similar) event:
                slideTransition(currentIndex - 1, prevWalk);
                slideTransition(currentIndex + 1, nextWalk);
            };

            // Determine whether to switch slides or snap back
            const handlePointerUp = (e) => {
                if (e.type === 'pointercancel') {
                    resetSlides();
                } else {
                    const shift =
                        (startX - e.pageX) /
                        (container.offsetWidth < 650
                            ? container.offsetWidth
                            : carousel.offsetWidth);
                    if (!isDragging) return;
                    if (shift >= 0.25 && currentIndex < slides.length - 1) {
                        changeSlide(currentIndex + 1, false);
                        isDragging = false;
                    } else if (shift <= -0.25 && currentIndex > 0) {
                        changeSlide(currentIndex - 1, false);
                        isDragging = false;
                    } else {
                        resetSlides();

                        isDragging = false;
                    }
                }

                document.removeEventListener('dragstart', handleDragStart);
                document.removeEventListener('pointermove', handlePointerMove);
                document.removeEventListener('pointerup', handlePointerUp);
                document.removeEventListener('pointercancel', handlePointerUp);
            };

            slides.forEach((slide, index) => {
                // Set up aria attributes on slides
                slide.setAttribute('role', 'group');
                slide.setAttribute(
                    'aria-roledescription',
                    text[lang].slideRoleDesc
                );
                slide.setAttribute(
                    'aria-label',
                    text[lang].slideLabel(index, slides.length)
                );

                // Remove links from initial tab order
                slide.querySelectorAll('a').forEach((a) => (a.tabIndex = '-1'));
                // Create a dot counter for each slide
                const dot = document.createElement('button');
                dot.classList.add('cs-dot');
                dot.setAttribute('type', 'button');
                dot.setAttribute(
                    'aria-label',
                    text[lang].dotLabel(index, slides.length)
                );
                dot.addEventListener('click', () => {
                    if (index !== currentIndex) changeSlide(index, true);
                });
                dotNav.appendChild(dot);
                dots.push(dot);

                // Set first slide as tab-navigable and the rest as not and hidden

                if (index !== 0) slide.toggleAttribute('inert');
                slide.tabIndex = index !== 0 ? '-1' : '0';

                // Handle mouse/touch and drag events for slides
                slide.addEventListener('pointerdown', (e) => {
                    if (currentIndex !== index) return;
                    isDragging = true;
                    startX = e.pageX;
                    offsetX = e.pageX - carousel.offsetLeft;

                    carousel.classList.remove('cs-tabbed');
                    document.addEventListener('dragstart', handleDragStart);
                    document.addEventListener('pointermove', handlePointerMove);
                    document.addEventListener('pointerup', handlePointerUp);
                    document.addEventListener('pointercancel', handlePointerUp);
                });
            });

            // Initialize dots, set height of tallent rich text content,
            // and remove pre-load hidden styling from slides
            updateDots();

            // Delay visibility to prevent animation shifts
            if (
                document.querySelector(
                    'link[rel="stylesheet"][href$="/carousel.css"]'
                )
            ) {
                setTimeout(() => {
                    carousel.classList.remove('cs-hidden');
                }, 300);
            }
        });

        // Update the carousel width if the window is resized at all
        // Includes orientation changes on mobile
        window.addEventListener('resize', updateCarouselSize);

        // Set baseline carousel width
        updateCarouselSize();
    });
};

carouselSetup();



